import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plug, LogIn, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Agents have no Administration access, so this is their only way to connect
   the Atlassian MCP that triage reads through. It sits in the header because
   it has to be reachable before a ticket is opened, not only after one fails. */
export default function AtlassianChip({ returnTo = "/agent" }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/triage/health`);
      setState(r.data);
    } catch {
      setState({ ready: false, detail: "Couldn't check the Atlassian connection" });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/triage/atlassian/connect`,
                                { params: { return_to: returnTo } });
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the Atlassian sign-in");
      setBusy(false);
    }
  };

  if (!state) return null;
  const ready = !!state.ready;

  return (
    <button
      onClick={ready ? load : connect}
      disabled={busy}
      title={ready
        ? `Triage reads Jira as ${state.identity?.email || "your Atlassian account"}`
        : state.detail || "Connect Atlassian so triage can read Jira and Confluence"}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        ready
          ? "bg-[#12301F] text-[#8FD6AE] hover:text-[#C7F0D9]"
          : "bg-[#3A2E05] text-[#FFD108] hover:text-[#FFE98A]"}`}
      data-testid="agent-atlassian-chip"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : ready ? <CheckCircle2 className="w-3.5 h-3.5" />
          : <AlertTriangle className="w-3.5 h-3.5" />}
      {ready ? "Atlassian connected" : "Connect Atlassian"}
      {!ready && !busy && <LogIn className="w-3 h-3" />}
      {ready && <Plug className="w-3 h-3 opacity-60" />}
    </button>
  );
}
